import { useEffect, useMemo, useState } from 'react'
import { SearchCriteriaFilterActions } from '@corratech/pylot-filters-and-sort'
import type {
    OutputInterface,
    ProductVariablesInterface
} from '@pylot-data/api/operations/get-product'
import { productDetailQuery } from '@pylot-data/hooks/product/graphql/productDetailQuery'
import { groupedProductDetailQuery } from '@pylot-data/hooks/product/graphql/groupedProductDetailQuery'
import { SwrUseQueryOutput, useQuery } from '@pylot-data/hooks/use-query'
import type { UseQuerySwrOptions } from '@pylot-data/hooks/use-query'
import { GiftCardTypeEnum } from '@pylot-data/enums/GiftCardTypeEnum'
import type { RequestInit } from '@vercel/fetch'
import type { SWRConfiguration } from 'swr'

export enum ProductType {
    CONFIGURABLE_PRODUCT = 'ConfigurableProduct',
    SIMPLE_PRODUCT = 'SimpleProduct',
    GROUPED_PRODUCT = 'GroupedProduct',
    GIFT_CARD_PRODUCT = 'GiftCardProduct',
    BUNDLE_PRODUCT = 'BundleProduct'
}

const SUPPORTED_PRODUCT_TYPES = [
    ProductType.SIMPLE_PRODUCT,
    ProductType.CONFIGURABLE_PRODUCT,
    ProductType.BUNDLE_PRODUCT,
    ProductType.GIFT_CARD_PRODUCT,
    ProductType.GROUPED_PRODUCT
] as const

type UseProductFetchOptions = {
    productUrl: string
    productQuery?: string
}

type GroupedVariables = { sku: string }
type AnyVariables = ProductVariablesInterface | GroupedVariables

type UseProductFetchReturn = SwrUseQueryOutput<OutputInterface> & {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    product: any
    isSupportedProductType: boolean
    isConfig: boolean
    isBundle: boolean
    isGiftCard: boolean
    isGrouped: boolean
}

export const useProductFetch = (
    { productUrl, productQuery = productDetailQuery }: UseProductFetchOptions,
    swrOptions?: UseQuerySwrOptions,
    fetchOptions?: RequestInit
): UseProductFetchReturn => {
    const initialProduct = (
        swrOptions?.initialData as
            { data?: Partial<OutputInterface> } | undefined
    )?.data?.productDetail?.items?.[0]

    const ssrGroupedSku =
        initialProduct?.__typename === ProductType.GROUPED_PRODUCT &&
        typeof initialProduct?.sku === 'string'
            ? (initialProduct.sku as string)
            : undefined

    // Modo mínimo: si estamos en grouped y con qué sku (si aplica)
    const [groupedSku, setGroupedSku] = useState<string | null>(
        ssrGroupedSku ?? null
    )

    // Query/variables derivadas (sin efectos de sync)
    const selectedQuery = groupedSku ? groupedProductDetailQuery : productQuery

    const selectedVariables: AnyVariables = useMemo(() => {
        if (groupedSku) return { sku: groupedSku }

        return {
            searchCriteria: [
                {
                    attribute_code: 'url_key',
                    filter_action: SearchCriteriaFilterActions.EQ,
                    filter_value: productUrl
                }
            ]
        }
    }, [groupedSku, productUrl])

    const SWRResult = useQuery<AnyVariables, OutputInterface>(
        selectedQuery,
        selectedVariables,
        { ...swrOptions },
        { method: 'POST', ...fetchOptions }
    )

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const rawData = SWRResult.data?.data as any | undefined

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const product: any =
        rawData?.productDetail?.items?.[0] ??
        rawData?.products?.items?.[0] ??
        undefined

    const productType = product?.__typename as ProductType | undefined
    const isConfig = productType === ProductType.CONFIGURABLE_PRODUCT
    const isBundle = productType === ProductType.BUNDLE_PRODUCT
    const isGrouped = productType === ProductType.GROUPED_PRODUCT
    const isGiftCard =
        productType === ProductType.GIFT_CARD_PRODUCT &&
        product?.giftcard_type === GiftCardTypeEnum.Virtual

    const isSupportedProductType = (
        SUPPORTED_PRODUCT_TYPES as readonly string[]
    ).includes(productType as (typeof SUPPORTED_PRODUCT_TYPES)[number])

    // Switch a grouped en cliente si el producto es grouped (igual que antes, pero solo setea sku)
    useEffect(() => {
        if (!isGrouped) return
        const sku = product?.sku
        if (typeof sku !== 'string' || sku.length === 0) return
        if (groupedSku === sku) return
        setGroupedSku(sku)
    }, [isGrouped, product?.sku, groupedSku])

    return {
        product,
        isSupportedProductType,
        isConfig,
        isBundle,
        isGiftCard,
        isGrouped,
        ...SWRResult
    }
}
